import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, patch } from '@/lib/api';
import { useUiStore } from '@/stores/ui.store';

export const sectionKeys = {
  all: ['sections'],
  list: (filters = {}) => ['sections', 'list', filters],
};

/**
 * The sections the caller may see. The API already scopes this list, so whatever comes
 * back is exactly what belongs in the switcher — the client does no filtering of its own
 * (I7: scoping is a data-layer concern, never a UI one).
 *
 * Each row carries `modules`: the **live** set for that section, core modules included
 * (docs/03 §2a). That is what the navigation filter reads.
 */
export const useSections = (options = {}) =>
  useQuery({
    queryKey: sectionKeys.list(),
    queryFn: async () => (await get('/sections')).data,
    // Sections change about once a year; no point re-fetching them on every focus.
    staleTime: 10 * 60_000,
    ...options,
  });

export const useSection = (id, options = {}) =>
  useQuery({
    queryKey: ['sections', id],
    queryFn: async () => (await get(`/sections/${id}`)).data,
    enabled: !!id,
    ...options,
  });

/**
 * Which modules are in force for what the user is currently looking at.
 *
 * One section active → that section's modules. **No** active section means the group
 * view, and there the honest answer is the union across every section they can see: a
 * CFO standing at group level does run the brokers module, in one of the four sections,
 * and hiding the deal pipeline from them because "no section is selected" would be a
 * worse lie than showing it.
 *
 * `null` while the query is in flight, which `isItemVisible` reads as "not known yet"
 * and does not filter on — a menu that blanks for a moment on every page load is a bug
 * report, and the API re-checks everything anyway (docs/03 §4, layer 5).
 */
export const useActiveModules = () => {
  const activeSectionId = useUiStore((state) => state.activeSectionId);
  const { data: sections, isSuccess } = useSections();

  return useMemo(() => {
    if (!isSuccess || !sections) return null;

    /*
     * An **empty** list is treated as unknown, not as "runs nothing". Core modules are
     * always in a section's live set, so a section that reports none did not answer the
     * question — an older API, or a fixture — and filtering the whole menu away on that
     * basis turns a version skew into a blank app.
     */
    const orNull = (list) => (list.length ? list : null);

    if (activeSectionId) {
      const active = sections.find((s) => s.id === activeSectionId);
      return orNull(active?.modules ?? []);
    }

    return orNull([...new Set(sections.flatMap((s) => s.modules || []))]);
  }, [isSuccess, sections, activeSectionId]);
};

/**
 * Which business line the user is standing in, by slug — what pins a section vertical to
 * its own menu (`navigation.config.js`).
 *
 * `null` for the group view *and* while the query is in flight, and the navigation filter
 * reads both the same way: show every vertical rather than none. The slug is the section's
 * identity rather than its configuration, which is why this is a second question from
 * `useActiveModules()` and not a field of its answer — Real Estate runs no vertical module
 * at all and still has a menu of its own (docs/10 §4.2).
 */
export const useActiveSectionSlug = () => {
  const activeSectionId = useUiStore((state) => state.activeSectionId);
  const { data: sections } = useSections();

  return useMemo(
    () => (activeSectionId ? (sections?.find((s) => s.id === activeSectionId)?.slug ?? null) : null),
    [sections, activeSectionId],
  );
};

/**
 * The section a **vertical's own screen** writes into.
 *
 * Every scoped document carries a `sectionId` (**I7**), and the switcher's group view leaves
 * `activeSectionId` null — a CFO standing at group level is not standing in any one section.
 * A form on a vertical screen still knows the answer, because the screen belongs to exactly
 * one business line: a broker profile written from the Brokers menu is Brokers', whether or
 * not the switcher happens to be pointed there. Without this the form posted `sectionId:
 * null` and the API answered "the submitted data is invalid", which tells the operator
 * nothing about a field the form never showed them.
 *
 * Falls back to the section in scope, so a section-scoped user is unaffected.
 */
export const useVerticalSectionId = (slug) => {
  const activeSectionId = useUiStore((state) => state.activeSectionId);
  const { data: sections } = useSections();

  return useMemo(
    () => activeSectionId ?? sections?.find((s) => s.slug === slug)?.id ?? null,
    [sections, activeSectionId, slug],
  );
};

/**
 * **The two internal controls** (docs/03 §3a) — segregation of duties, and the mandatory
 * expense receipt.
 *
 * A partial patch, unlike the module list: they are independent switches, so changing one
 * says nothing about the other. Invalidates the whole section tree for the same reason
 * `useSetSectionModules` does — the approval path reads these, and a stale copy would let a
 * screen offer a button the API is about to refuse.
 */
export const useSetSectionControls = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...controls }) => (await patch(`/sections/${id}/controls`, controls)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sectionKeys.all }),
  });
};

/** Replace a section's module set (`section.settings`, HQ-level — docs/03 §2a). */
export const useSetSectionModules = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, modules }) => (await patch(`/sections/${id}/modules`, { modules })).data,
    onSuccess: () => {
      /*
       * Everything the shell renders is downstream of this: the sidebar, the command
       * palette and every screen's own gating. Invalidating the whole tree is cheaper
       * than reasoning about which query held a stale module list.
       */
      queryClient.invalidateQueries({ queryKey: sectionKeys.all });
    },
  });
};

export default useSections;
